import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { GenericDialog } from "@/components/dialogs";
import { highlight } from "@/util";
import { QualifierUser } from "@/api/user";
import { useDebounce } from "@/hooks";
import { useState } from "react";
import { useUserLookup } from "../hooks";

import styles from "./UserSelection.tsx.module.css";

export type UserSelectionProps = {
  initialUser?: QualifierUser;
  onAccept: (user: QualifierUser) => void;
};

export function UserSelectionDialog(props: UserSelectionProps) {
  const [selectedUser, setSelectedUser] = useState(props.initialUser);
  const [search, setSearch] = useState("");

  const debouncedSearch = useDebounce(search, 300);
  const users = useUserLookup(debouncedSearch);

  function handleSelectUser() {
    if (selectedUser) {
      props.onAccept(selectedUser);
    }
  }

  return (
    <GenericDialog caption="Select User" buttons="OKCancel" className={styles.dialog} onAccept={handleSelectUser}>
      <>
        <div className="input-group" style={{ maxWidth: 400 }}>
          <input
            type="text"
            className="form-control"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
          <span className="input-group-text" role="button" onClick={() => users.refetch()}>
            <FontAwesomeIcon size="sm" icon={faMagnifyingGlass} title="Search" />
          </span>
        </div>

        <div className={styles.container}>
          {users.isLoading ? (
            <p>Loading...</p>
          ) : users.isError ? (
            <p>An unknown error occurred.</p>
          ) : debouncedSearch && users.data?.length === 0 ? (
            <p>Your search returned no results.</p>
          ) : users.data?.length === 0 ? (
            <p>No users found.</p>
          ) : (
            <table className="table table-sm table-borderless">
              <thead className="table-light">
                <tr>
                  <th>User Name</th>
                  <th className="d-none d-md-table-cell">Login Name</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {users.data?.map((user) => (
                  <tr key={user.id}>
                    <td>{highlight(user.name ?? user.userName, debouncedSearch)}</td>
                    <td className="d-none d-md-table-cell">{highlight(user.userName, debouncedSearch)}</td>
                    <td className="text-end pe-4">
                      <input
                        type="radio"
                        className="form-check-input"
                        checked={user.id === selectedUser?.id}
                        onChange={() => setSelectedUser(user)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </>
    </GenericDialog>
  );
}
